import { format } from "date-fns";
import { Calendar as CalendarIcon, DollarSign, ShoppingCart, Target, Wallet } from "lucide-react";
import { useGetIdentity, useGetList } from "ra-core";
import * as React from "react";

import { DocumentPipelineView } from "./DocumentPipelineView";
import { ExpenseTracker } from "./ExpenseTracker";
import { FinancialMetricCard } from "./FinancialMetricCard";
import { PaymentReminderCard } from "./PaymentReminderCard";
import { ReceivablesAgingWidget } from "./ReceivablesAgingWidget";
import { RecentActivityCard } from "./RecentActivityCard";
import { SalesPipelineWidget } from "./SalesPipelineWidget";
import { StockMovementWidget } from "./StockMovementWidget";
import { TrendChart } from "./TrendChart";

import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

export const Dashboard = () => {
  const { data: identity } = useGetIdentity();
  const [date, setDate] = React.useState<Date | undefined>(new Date());
  const [period, setPeriod] = React.useState<'Daily' | 'Weekly' | 'Monthly' | 'Yearly'>('Monthly');

  // ─── Live Metrics ──────────────────────────────────────────────────────
  const { data: invoices } = useGetList("customer_invoices", {
    pagination: { page: 1, perPage: 500 },
    filter: {},
  });
  const { data: purchaseOrders } = useGetList("purchase_orders", {
    pagination: { page: 1, perPage: 500 },
    filter: {},
  });
  const { data: opportunities } = useGetList("opportunities", {
    pagination: { page: 1, perPage: 500 },
    filter: {},
  });
  const { data: aging } = useGetList("customer_aging_view", {
    pagination: { page: 1, perPage: 500 },
    filter: {},
  });

  // ─── Calculated Metrics ─────────────────────────────────────────────────
  const totalRevenue = invoices?.reduce((sum, inv) => sum + (inv.total_amount || 0), 0) ?? 0;
  const paidRevenue = invoices?.reduce((sum, inv) => sum + (inv.paid_amount || 0), 0) ?? 0;
  const revenueProgress = totalRevenue > 0 ? Math.round((paidRevenue / totalRevenue) * 100) : 0;

  const totalPOValue = purchaseOrders?.reduce((sum, po) => sum + (po.total_amount || 0), 0) ?? 0;
  const pendingPOCount = purchaseOrders?.filter((po) => po.status === "draft" || po.status === "sent").length ?? 0;

  const outstandingReceivables = aging?.reduce((sum, row) => sum + (Number(row.total_outstanding) || 0), 0) ?? 0;
  const breachCount = aging?.filter((row) => row.breach_flag).length ?? 0;

  const openPipelineValue = (opportunities || [])
    .filter((o) => o.stage !== "won" && o.stage !== "lost")
    .reduce((sum, o) => sum + (o.deal_value || 0), 0);
  const openOppCount = (opportunities || []).filter((o) => o.stage !== "won" && o.stage !== "lost").length;

  // ─── Trend Chart Data (dynamic based on period) ────────────
  const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const now = new Date();

  const getEarningsData = () => {
    if (period === 'Daily') {
      return Array.from({ length: 14 }, (_, i) => {
        const date = new Date(now.getFullYear(), now.getMonth(), now.getDate() - (13 - i));
        const dayStr = date.toISOString().split("T")[0];
        const label = date.toLocaleDateString("en-IN", { day: "numeric", month: "short" });
        const income = invoices?.filter((inv) => inv.date?.startsWith(dayStr)).reduce((s, inv) => s + (inv.total_amount || 0), 0) ?? 0;
        const expense = purchaseOrders?.filter((po) => po.date?.startsWith(dayStr)).reduce((s, po) => s + (po.total_amount || 0), 0) ?? 0;
        return { label, income, expense };
      });
    } else if (period === 'Weekly') {
      return Array.from({ length: 8 }, (_, i) => {
        const endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - (7 - i) * 7);
        endDate.setDate(endDate.getDate() + (6 - endDate.getDay())); // end of that week
        const startDate = new Date(endDate);
        startDate.setDate(endDate.getDate() - 6); // start of week
        const label = `Wk ${endDate.getDate()} ${monthNames[endDate.getMonth()]}`;
        const income = invoices?.filter((inv) => {
          const d = new Date(inv.date); return d >= startDate && d <= endDate;
        }).reduce((s, inv) => s + (inv.total_amount || 0), 0) ?? 0;
        const expense = purchaseOrders?.filter((po) => {
          const d = new Date(po.date); return d >= startDate && d <= endDate;
        }).reduce((s, po) => s + (po.total_amount || 0), 0) ?? 0;
        return { label, income, expense };
      });
    } else if (period === 'Yearly') {
      return Array.from({ length: 3 }, (_, i) => {
        const year = now.getFullYear() - (2 - i);
        const label = `${year}`;
        const income = invoices?.filter((inv) => new Date(inv.date).getFullYear() === year).reduce((s, inv) => s + (inv.total_amount || 0), 0) ?? 0;
        const expense = purchaseOrders?.filter((po) => new Date(po.date).getFullYear() === year).reduce((s, po) => s + (po.total_amount || 0), 0) ?? 0;
        return { label, income, expense };
      });
    } else { // Monthly
      return Array.from({ length: 6 }, (_, i) => {
        const date = new Date(now.getFullYear(), now.getMonth() - (5 - i), 1);
        const label = monthNames[date.getMonth()];
        const year = date.getFullYear();
        const m = date.getMonth();
        const income = invoices?.filter((inv) => {
          const d = new Date(inv.date); return d.getMonth() === m && d.getFullYear() === year;
        }).reduce((s, inv) => s + (inv.total_amount || 0), 0) ?? 0;
        const expense = purchaseOrders?.filter((po) => {
          const d = new Date(po.date); return d.getMonth() === m && d.getFullYear() === year;
        }).reduce((s, po) => s + (po.total_amount || 0), 0) ?? 0;
        return { label, income, expense };
      });
    }
  };
  const earningsData = getEarningsData();

  // ─── Expense Tracker (dynamic spend from POs) ──────────
  const getExpenseData = () => {
    if (period === 'Daily' || period === 'Weekly') {
      return Array.from({ length: 7 }, (_, i) => {
        const target = new Date(now.getFullYear(), now.getMonth(), now.getDate() - (6 - i));
        const dayStr = target.toISOString().split("T")[0];
        const category = target.toLocaleDateString("en-IN", { weekday: "short" });
        const amount = purchaseOrders?.filter((po) => po.date?.startsWith(dayStr)).reduce((s, po) => s + (po.total_amount || 0), 0) ?? 0;
        return { category, amount };
      });
    } else if (period === 'Yearly') {
      return Array.from({ length: 6 }, (_, i) => {
        const date = new Date(now.getFullYear(), now.getMonth() - (5 - i), 1);
        const category = monthNames[date.getMonth()];
        const year = date.getFullYear();
        const m = date.getMonth();
        const amount = purchaseOrders?.filter((po) => {
          const d = new Date(po.date); return d.getMonth() === m && d.getFullYear() === year;
        }).reduce((s, po) => s + (po.total_amount || 0), 0) ?? 0;
        return { category, amount };
      });
    } else { // Monthly
      return Array.from({ length: 4 }, (_, i) => {
        const endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - (3 - i) * 7);
        const startDate = new Date(endDate);
        startDate.setDate(endDate.getDate() - 6);
        const category = `Wk ${3 - i + 1} ago`;
        const amount = purchaseOrders?.filter((po) => {
          const d = new Date(po.date); return d >= startDate && d <= endDate;
        }).reduce((s, po) => s + (po.total_amount || 0), 0) ?? 0;
        return { category, amount };
      });
    }
  };
  const expenseData = getExpenseData();

  // ─── Recent Activities (last 8 records across invoices & POs) ────────
  const invoiceActivities = (invoices || []).slice(0, 4).map((inv) => ({
    id: Number(inv.id),
    type: "income" as const,
    description: `Invoice to ${inv.customer_name || "Customer"}`,
    amount: inv.total_amount || 0,
    date: inv.date ? new Date(inv.date).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "",
    category: `${inv.reference}`,
    link: `/customer_invoices/${inv.id}/show`,
  }));

  const poActivities = (purchaseOrders || []).slice(0, 4).map((po) => ({
    id: Number(po.id) + 10000,
    type: "expense" as const,
    description: `PO to ${po.vendor_name || "Vendor"}`,
    amount: po.total_amount || 0,
    date: po.date ? new Date(po.date).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "",
    category: `${po.reference}`,
    link: `/purchase_orders/${po.id}/show`,
  }));

  const recentActivities = [...invoiceActivities, ...poActivities]
    .sort((a, b) => (b.date > a.date ? 1 : -1))
    .slice(0, 6);

  // ─── Render ─────────────────────────────────────────────────────────────
  return (
    <div className="h-full overflow-y-auto scrollbar-hide">
      <div className="space-y-4 pt-4 pb-6">
        {/* Welcome Header */}
        <div className="flex items-center justify-between gap-4">
          <div>
            <h1 className="text-xl font-bold text-foreground">
              Hello, {identity?.fullName ?? "there"}
            </h1>
            <p className="text-sm text-muted-foreground">Here's how the business is doing.</p>
          </div>
          {/* Dashboard Top Actions */}
          <div className="flex items-center gap-3 bg-card p-2 px-4 rounded-full border border-border shrink-0 shadow-sm">
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="ghost"
                  className="h-9 justify-start font-normal rounded-full px-4 hover:bg-muted"
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {date ? format(date, "PPP") : <span>Pick a date</span>}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 rounded-2xl border-border shadow-lg" align="end">
                <Calendar
                  mode="single"
                  selected={date}
                  onSelect={setDate}
                  initialFocus
                  className="rounded-2xl"
                  classNames={{
                    day: "h-9 w-9 p-0 font-normal rounded-full aria-selected:opacity-100 hover:bg-sidebar-accent transition-colors",
                    day_selected: "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground",
                    day_today: "font-semibold",
                    head_cell: "text-muted-foreground rounded-md w-9 font-normal text-[0.8rem]",
                  }}
                />
              </PopoverContent>
            </Popover>
            <div className="h-6 w-px bg-border mx-1 hidden md:block"></div>
            <div className="flex items-center gap-1 bg-muted p-1 rounded-full border border-border">
              {(['Daily', 'Weekly', 'Monthly', 'Yearly'] as const).map((p) => (
                <button
                  key={p}
                  onClick={() => setPeriod(p)}
                  className={`px-3 py-1 text-xs font-semibold rounded-full transition-all ${period === p
                    ? 'bg-card text-primary shadow-sm'
                    : 'text-muted-foreground hover:text-primary'
                    }`}
                >
                  {p}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* KPI Strip — the four numbers a business owner checks first */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <FinancialMetricCard
            title="Total Revenue Billed"
            amount={totalRevenue}
            icon={DollarSign}
            trend="up"
            trendValue={`${revenueProgress}% collected`}
            color="success"
          />
          <FinancialMetricCard
            title="Outstanding Receivables"
            amount={outstandingReceivables}
            icon={Wallet}
            trend={breachCount > 0 ? "down" : "up"}
            trendValue={breachCount > 0 ? `${breachCount} over credit limit` : "within limits"}
            color={breachCount > 0 ? "destructive" : "warning"}
          />
          <FinancialMetricCard
            title="Open Pipeline Value"
            amount={openPipelineValue}
            icon={Target}
            trend="up"
            trendValue={`${openOppCount} open deals`}
            color="primary"
          />
          <FinancialMetricCard
            title="Procurement Spend"
            amount={totalPOValue}
            icon={ShoppingCart}
            trend={pendingPOCount > 5 ? "down" : "up"}
            trendValue={`${pendingPOCount} POs pending`}
            color="info"
          />
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
          {/* Left Column - Charts & Pipeline */}
          <div className="lg:col-span-8 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <SalesPipelineWidget />
              <ReceivablesAgingWidget />
            </div>
            <TrendChart
              title={`Revenue vs Expense (${period})`}
              subtitle="Based on invoices and purchase orders"
              data={earningsData}
            />
            <ExpenseTracker title={`PO Spending — ${period === 'Daily' || period === 'Weekly' ? 'Last 7 Days' : period === 'Monthly' ? 'Last 4 Weeks' : 'Last 6 Months'}`} data={expenseData} />
          </div>

          {/* Right Column - Alerts & Activity */}
          <div className="lg:col-span-4 space-y-4">
            <PaymentReminderCard />
            <RecentActivityCard
              title="Recent Transactions"
              activities={recentActivities}
            />
            <DocumentPipelineView />
            <StockMovementWidget />
          </div>
        </div>
      </div>
    </div>
  );
};
